import { FC, useCallback } from 'react'
import { useController } from 'react-hook-form'

import { isSanctionedCountryWithMediumRisk } from '@freighthub/reference-master-data'

import { AddressSelector } from './AddressSelector'
import { AddressFormData } from './schema'

type AddressFormSelectorPropsBase = {
  name: string
  label?: string
  required?: boolean
}

/**
 * AddressFormSelectorProps
 * – If `enableSanctionRiskCheck` is `true`, the warning message is mandatory.
 */
export type AddressFormSelectorProps =
  | (AddressFormSelectorPropsBase & {
      enableSanctionRiskCheck: true // literal true
      sanctionRiskWarningMessage: string // **required**
    })
  | (AddressFormSelectorPropsBase & {
      enableSanctionRiskCheck?: false // false or omitted
      sanctionRiskWarningMessage?: never // not allowed / omitted
    })

export const AddressFormSelector: FC<AddressFormSelectorProps> = (props) => {
  const { name, label, required, enableSanctionRiskCheck, sanctionRiskWarningMessage } = props

  const {
    field: { ref, onChange, value },
    fieldState: { error },
  } = useController({ name })

  const warning =
    !!enableSanctionRiskCheck &&
    value?.address?.countryCode &&
    isSanctionedCountryWithMediumRisk(value?.address?.countryCode)
      ? sanctionRiskWarningMessage
      : undefined

  // Do not remove this useCallback as this onChange is being used inside useEffect
  const onAddressChange = useCallback(
    (selectedAddress: AddressFormData | null) => {
      onChange(selectedAddress)
    },
    [onChange]
  )

  return (
    <AddressSelector
      label={label}
      required={required}
      inputRef={ref}
      error={error}
      onChange={onAddressChange}
      value={value}
      warning={warning}
    />
  )
}
